"use client";

import React, { useState, useEffect, useMemo, Children, isValidElement } from "react";
import { ProblemHistoryProvider } from "@/components/ProblemHistoryContext";
import { ProblemDataProvider } from "@/components/ProblemDataContext";
import { ChoicesVisibilityProvider } from "@/components/ChoicesVisibilityContext";
import { CodeLangProvider } from "@/components/CodeLangContext";
import ArticleChoicesToggle from "@/components/ArticleChoicesToggle";
import { getProblemHistoriesBatch, getProblemHistoriesBatchBySessionId } from "@/lib/server-actions/problem/read";
import { getAnonymousSessionId } from "@/lib/anonymousSession";
import { useUser } from "@clerk/nextjs";
import { ProblemHistory } from "@/lib/types";
import { useProblemsQuery } from "@/hooks/useProblemQuery";

interface MDXContentWrapperProps {
  children: React.ReactNode;
}

/**
 * React element 의 컴포넌트 표시 이름을 구한다. 일반 함수 컴포넌트뿐 아니라
 * forwardRef(예: RandomProblems)·memo 같은 exotic 타입(typeof 가 'object')도 풀어
 * displayName 을 읽는다 — RandomProblems 는 React.forwardRef 라 단순 name 추출에서 누락되어
 * hasOwnProvider 감지를 우회하면 ChoicesVisibilityProvider 가 이중 마운트될 수 있다.
 */
function getComponentDisplayName(type: unknown): string {
  if (typeof type === "function") {
    const fn = type as { displayName?: string; name?: string };
    return fn.displayName || fn.name || "";
  }
  if (type && typeof type === "object") {
    const exotic = type as { displayName?: string; render?: unknown; type?: unknown };
    return (
      exotic.displayName ||
      getComponentDisplayName(exotic.render) ||
      getComponentDisplayName(exotic.type) ||
      ""
    );
  }
  return "";
}

/**
 * MDX 콘텐츠를 감싸서 ProblemAnswer/ProblemAnswerFetcher 컴포넌트들의
 * 이력과 문제 데이터를 배치 조회하는 래퍼
 *
 * 문제점: MDX에 ProblemAnswer/ProblemAnswerFetcher가 많을 경우 각각 개별 API 호출 (N+1 문제)
 * 해결: children에서 문제 ID를 추출하여 한 번에 배치 조회
 *
 * 주의: RandomizedProblems처럼 자체 Provider가 있는 컴포넌트가 있으면
 *       이 래퍼의 Provider를 적용하지 않음 (중첩 방지)
 */
export default function MDXContentWrapper({ children }: MDXContentWrapperProps) {
  const [problemHistories, setProblemHistories] = useState<Record<string, ProblemHistory | null>>({});
  const [isLoadingHistories, setIsLoadingHistories] = useState(true);
  const { isSignedIn, isLoaded: isUserLoaded, user } = useUser();

  // children에서 ProblemAnswer/ProblemAnswerFetcher의 id prop 추출
  // RandomizedProblems 같은 래퍼 컴포넌트가 있는지도 확인
  const { problemIds, hasOwnProvider } = useMemo(() => {
    const ids: string[] = [];
    let foundOwnProvider = false;

    const traverse = (node: React.ReactNode) => {
      if (isValidElement(node)) {
        const props = node.props as { id?: string; children?: React.ReactNode };
        const nodeType = node.type;

        // RandomizedProblems 또는 RandomProblems 컴포넌트가 있는지 확인
        // (RandomProblems 는 forwardRef exotic 이라 getComponentDisplayName 으로 풀어야 감지됨)
        const typeName = getComponentDisplayName(nodeType);
        if (typeName === 'RandomizedProblems' || typeName === 'RandomProblems') {
          foundOwnProvider = true;
        }

        // 문제 ID만 추출 (문제 ID는 항상 '_'를 포함하고, 헤딩 ID는 createId()에서 '_'가 제거됨)
        if (props?.id && typeof props.id === 'string' && props.id.includes('_')) {
          ids.push(props.id);
        }

        // children 재귀 탐색
        if (props?.children) {
          Children.forEach(props.children, traverse);
        }
      } else if (Array.isArray(node)) {
        node.forEach(traverse);
      }
    };

    Children.forEach(children, traverse);
    return { problemIds: ids, hasOwnProvider: foundOwnProvider };
  }, [children]);

  // React Query로 문제 데이터 조회 (IndexedDB 캐싱 포함)
  const { data: problemData, isLoading: isLoadingProblems } = useProblemsQuery(
    hasOwnProvider ? [] : problemIds
  );

  // "보기 숨기기" 토글 노출 여부 판단 (JCG-2312) — 보기(선택지)가 정답을 그대로 드러내는 문제가
  // 페이지에 하나라도 있을 때만 글 페이지에 토글 + ChoicesVisibilityProvider 를 붙인다.
  // 제외 조건은 ProblemAnswer 의 전체 숨기기 제외 로직과 동일: choiceCopy(보기 입력 보조)·
  // alwaysShowChoices(보기가 문제 필수 요소)·answerSeparator(보기로 답변란 채우기)는 보기가
  // 풀이의 일부라 숨기기 대상이 아니다. RandomProblems 페이지(hasOwnProvider)는 자체 토글 보유 → 제외.
  // answerSeparator 는 separatorKeys 가 있는 일반적 케이스(correctAnswer 가 object/answer-sets)를
  // 가정해 제외한다 — ProblemAnswer 의 isAnswerSeparatorWithChoices 와 정렬(현재 데이터 전부 일치).
  const hasHideableChoiceProblem = useMemo(() => {
    if (hasOwnProvider || !problemData) return false;
    return problemIds.some((id) => {
      const p = problemData[id];
      if (!p || !p.choices || p.choices.length === 0) return false;
      if (p.choiceCopy || p.alwaysShowChoices || p.answerSeparator) return false;
      return true;
    });
  }, [hasOwnProvider, problemData, problemIds]);

  // 문제 이력 배치 조회 — 외부 데이터 fetch, JCG-2034 마이그레이션 예정
  useEffect(() => {
    // 자체 Provider가 있거나 문제 ID가 없으면 로딩 완료로 표시
    if (hasOwnProvider || problemIds.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsLoadingHistories(false);
      return;
    }

    // Clerk 로딩이 완료될 때까지 대기
    if (!isUserLoaded) {
      return;
    }

    const loadHistories = async () => {
      setIsLoadingHistories(true);
      try {
        if (isSignedIn && user) {
          // 로그인 사용자: userId 기반 배치 조회
          const result = await getProblemHistoriesBatch(problemIds);
          if (result.success) {
            setProblemHistories(result.histories);
          }
        } else {
          // 비로그인 사용자: sessionId 기반 배치 조회
          const sessionId = getAnonymousSessionId();
          if (sessionId) {
            const result = await getProblemHistoriesBatchBySessionId(sessionId, problemIds);
            if (result.success) {
              setProblemHistories(result.histories);
            }
          }
        }
      } catch (err) {
        console.error("문제 이력 배치 조회 실패:", err);
      } finally {
        setIsLoadingHistories(false);
      }
    };

    loadHistories();
  }, [problemIds, hasOwnProvider, isUserLoaded, isSignedIn, user]);

  // 자체 Provider가 있는 컴포넌트가 있으면 래퍼의 Provider 적용하지 않음.
  // 문제 전용 페이지(왕감자시험·past-exam·문제 모음, RandomProblems 계열)의 상하 단계 이동은
  // PostLayout 의 ArticleScrollStepper(우하단 FAB)가 DOM 기반으로 처리하므로 여기서는 추가 렌더 없음(JCG-3210).
  if (hasOwnProvider) {
    return <>{children}</>;
  }

  // ChoicesVisibilityProvider 는 분기와 무관하게 항상 트리 최상단에 둔다 — 데이터가 비동기로
  // 도착해 hasHideableChoiceProblem 이 false→true 로 바뀌어도 반환 element 타입이 고정이라
  // 하위 ProblemDataProvider/ProblemAnswer 들이 remount 되지 않는다. "전체 보기 숨기기" 토글만
  // 조건부로 렌더한다(숨길 보기가 있는 문제가 있을 때만, 그리고 프리미엄 게이트는 토글 내부에서).
  // 데스크탑 셸 카드 + 모바일 하단 dock 토글을 모두 ArticleChoicesToggle 이 담당한다(JCG-3210).
  // ProblemAnswer 는 이미 globalChoicesHidden 을 읽어 "전체 보기 숨김" 오버레이를 그리므로
  // ProblemAnswer 수정 없이 글 페이지에서도 보기 숨기기가 동작한다. 상하 단계 이동은 PostLayout 의
  // ArticleScrollStepper(우하단 FAB)가 담당한다.
  return (
    <CodeLangProvider>
      <ChoicesVisibilityProvider>
        {hasHideableChoiceProblem && <ArticleChoicesToggle />}
        <ProblemDataProvider problems={problemData ?? {}} isLoading={isLoadingProblems}>
          <ProblemHistoryProvider histories={problemHistories} isLoading={isLoadingHistories}>
            {children}
          </ProblemHistoryProvider>
        </ProblemDataProvider>
      </ChoicesVisibilityProvider>
    </CodeLangProvider>
  );
}
